import { useState, useEffect } from "react"
import noop from "lodash/noop"
import Button from "component-lib/Base/components/BaseButton/BaseButton"
import getContentUrl from "utils-lib/getContentUrl"
import { makeStyles } from "tss-react/mui"
import { ReactSVG } from "react-svg"
import closest from "utils-lib/closest"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { withLocalization } from "hoc-lib/localization"
import { Configure, InstantSearch } from "react-instantsearch"
import { searchClient } from "constants-lib/algoliaSearchClient"
import { getWindow } from "utils-lib/getWindow"
import { CustomAutoCompleteDesktop } from "component-lib/Algolia"
import { handleEnterPress } from "utils-lib/handleEnterPress"

export const strings = {
  en_CA: {
    ESC: `Esc`,
  },
  fr_CA: {
    ESC: `Esc`, // translate: true
  },
}

const useStyles = makeStyles()(() => ({
  selectedIndexActive: {
    borderBottom: `2px solid green !important`,
  },
  esc: {
    top: -6,
  },
  closeIcon: {
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
}))

const AlgoliaSearch = ({
  indexList,
  numCharacters,
  numResults,
  searchBarPlaceholderText,
  strings,
  suggestionIndex,
  queryIndex,
  numHighlightedResults,
  numberQueries,
  errorMessageTitle,
  errorMessageDescription,
  failureTitle,
  failureDescription,
  suggestedText,
  resultsText,
  seeAllResultsText,
  searchDelay,
  language,
  onClose = noop,
  resultsIndex,
  filterType,
}) => {
  const [search, setSearch] = useState(``)

  const ssrWindow = getWindow()

  useEffect(() => {
    ssrWindow.document.body.style.overflow = "hidden"

    ssrWindow.document.addEventListener("click", handleSearchClick)

    return () => {
      ssrWindow.document.body.style.overflow = "auto"

      ssrWindow.document.removeEventListener("click", handleSearchClick)
    }
  }, [])

  function handleSearchClick(e) {
    if (!closest(e.target, `.CustomAutoCompleteDesktop`)) {
      onClose()
    }
  }

  const { classes } = useStyles()

  const languageRoute = getLanguageRoute(language)

  return (
    <div className="d-flex w-100" data-testid="AlgoliaSearch">
      <form
        className="flex-grow-1"
        onSubmit={(e) => {
          e.preventDefault()

          ssrWindow.location = `${languageRoute}/search?q=${encodeURIComponent(
            search,
          )}`
        }}
        autoComplete="off"
      >
        <InstantSearch
          searchClient={searchClient}
          indexName={suggestionIndex}
          future={{ preserveSharedStateOnUnmount: true }}
          insights
        >
          <CustomAutoCompleteDesktop
            onClose={onClose}
            search={search}
            setSearch={setSearch}
            resultsIndex={resultsIndex}
            suggestionIndex={suggestionIndex}
            queryIndex={queryIndex}
            filterType={filterType}
            searchBarPlaceholderText={searchBarPlaceholderText}
            numResults={numResults}
            numHighlightedResults={numHighlightedResults}
            numberQueries={numberQueries}
            numCharacters={numCharacters}
            errorMessageTitle={errorMessageTitle}
            errorMessageDescription={errorMessageDescription}
            failureTitle={failureTitle}
            failureDescription={failureDescription}
            suggestedText={suggestedText}
            resultsText={resultsText}
            seeAllResultsText={seeAllResultsText}
            searchDelay={searchDelay}
            indexList={indexList}
          />
          <Configure analyticsTags={["header"]} />
        </InstantSearch>
      </form>
      <Button
        onClick={onClose}
        analytics="global_search_searchbox_exit_esc_click"
        className={`mt-4 d-flex flex-column align-items-end wm-cursor-pointer a-searchTaggable ${classes.closeIcon}`}
        onBlur={onClose}
        onKeyDown={(e) => handleEnterPress(e, onClose)}
      >
        <ReactSVG
          wrapper="span"
          className="flex-shrink-0"
          src={getContentUrl("/content/dam/mywm/icons/green-close-x-icon.svg")}
        />
        <div className={`position-relative wm-link ${classes.esc}`}>
          {strings.ESC}
        </div>
      </Button>
    </div>
  )
}

// // Note: React 19 no-longer supports propTypes. Leaving this for reference.
// AlgoliaSearchLegacy.propTypes = {
//   indexList: PropTypes.array.isRequired,
//   numCharacters: PropTypes.number.isRequired,
//   numResults: PropTypes.number.isRequired,
//   searchBarPlaceholderText: PropTypes.string.isRequired,
//   strings: PropTypes.object.isRequired,
//   suggestionIndex: PropTypes.string.isRequired,
//   queryIndex: PropTypes.string.isRequired,
//   numHighlightedResults: PropTypes.number.isRequired,
//   numberQueries: PropTypes.number.isRequired,
//   errorMessageTitle: PropTypes.string.isRequired,
//   errorMessageDescription: PropTypes.string.isRequired,
//   failureTitle: PropTypes.string.isRequired,
//   failureDescription: PropTypes.string.isRequired,
//   suggestedText: PropTypes.string.isRequired,
//   resultsText: PropTypes.string.isRequired,
//   seeAllResultsText: PropTypes.string.isRequired,
//   searchDelay: PropTypes.number.isRequired,
//   language: PropTypes.string.isRequired,
//   onClose: PropTypes.func,
//   resultsIndex: PropTypes.string.isRequired,
//   filterType: PropTypes.string.isRequired,
// }

// // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// AlgoliaSearchLegacy.defaultProps = {
//   onClose: noop,
// }

export default withLocalization(strings)(AlgoliaSearch)
